import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NEVER,
  Subject,
  catchError,
  combineLatest,
  mergeMap,
  of,
  tap,
  throwError
} from 'rxjs';
import { IFolder } from 'src/app/core/entities/product-folder/interface/product-folder.interface';
import { SOCKET_PRODUCT_EVENT } from '../../enums/events-buffer/socket/socket-product-event.enum';
import { SOCKET_ROOM } from '../../enums/events-buffer/socket/socket-rooms-enum';
import { Logger } from '../../interfaces/Logger';
import { ISocketPayload } from '../../interfaces/socket-payload.interface';
import { SocketRoomHandlerService } from '../../services/socket/socket-room-handler.service';
import { SocketService } from '../../services/socket/socket.service';
import { ProductFolderService } from '../product-folder/product-folder.service';
import { SignatureService } from '../service/signature.service';
import { Product } from './interface/product.model';
import { ProductFactoryService } from './product-factory.service';
import { ProductService } from './product.service';

/**
 * Handles all product-related socket events and updates the local state accordingly.
 * Listens to product creation, activation, and folder modification events via websockets.
 * Ensures state consistency and signature validation for real-time product updates.
 * Implements best practices for Angular, RxJS, and SOLID principles.
 * @remarks
 * This service is intended to be provided in root and is optimized for performance and maintainability.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductSocketHandlerService {
  private readonly socketService = inject(SocketService);
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly productsService = inject(ProductService);
  private readonly productFolderService = inject(ProductFolderService);
  private readonly logger = inject(Logger);
  private readonly signatureService = inject(SignatureService);
  private readonly productFactoryService = inject(ProductFactoryService);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed$: Subject<void> = new Subject<void>();

  private onProductCreated: Subject<void> = new Subject<void>();
  onProductCreated$ = this.onProductCreated.asObservable();

  private onProductActivated: Subject<Product> = new Subject<Product>();
  onProductActivated$ = this.onProductActivated.asObservable();

  private onProductFoldersModified: Subject<void> = new Subject<void>();
  onProductFoldersModified$ = this.onProductFoldersModified.asObservable();

  constructor() {
    this.checkForProductRoom();
  }

  /**
   * Clears all active subscriptions and signals destruction for cleanup.
   * Should be called to prevent memory leaks when the service is no longer needed.
   */
  clearSubscriptions(): void {
    this.destroyed$.next();
  }

  /**
   * Notifies subscribers that a new product has been created via socket event.
   * Used internally to emit product creation events.
   */
  private notifyProductCreated(): void {
    this.onProductCreated.next();
  }

  /**
   * Notifies subscribers that a product has been activated or deactivated via socket event.
   * @param product The product that was activated or deactivated.
   */
  private notifyProductActivated(product: Product): void {
    this.onProductActivated.next(product);
  }

  /**
   * Notifies subscribers that product folders have been modified via socket event.
   * Used internally to emit folder modification events.
   */
  private notifyProductFolderModified(): void {
    this.onProductFoldersModified.next();
  }

  /**
   * Subscribes to the product socket room and initializes all product-related listeners.
   * Ensures listeners are only set up when the socket room is joined.
   */
  private checkForProductRoom(): void {
    this.socketRoomHandlerService
      .listenToRoom(SOCKET_ROOM.products)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // Clear previous subscriptions before reinitializing to avoid duplicates
        tap(() => {
          this.logger.debug(
            `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.products} 🍺] [Room joined, clearing previous subscriptions before reinitializing]`
          );
          this.clearSubscriptions();
        })
      )
      .subscribe(() => {
        this.initializeProductListeners();
      });
  }

  /**
   * Initializes all product-related socket event listeners (created, activated, folders modified).
   * Should only be called once the socket room is joined.
   */
  private initializeProductListeners(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.products} 🍺] [Product listeners initialized]`
    );
    this.onProductCreatedListener();
    this.onProductActivatedListener();
    this.onProductFoldersModifiedListener();
  }

  /**
   * Listens for product creation events from the socket and updates the local state accordingly.
   * Handles signature validation and ensures no duplicate processing.
   * Notifies subscribers when a new product is created.
   */
  private onProductCreatedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.products} 🍺] [Product created listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_PRODUCT_EVENT.created)
      .pipe(
        catchError((error) => throwError(() => error)),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest({
            eventResponse: of(eventResponse),
            currentSignature: this.signatureService.getSignatureFromIndexedDB(),
            product: this.productFactoryService.createOne(
              eventResponse.payload.product,
              true
            ),
          })
        ),
        mergeMap(({ eventResponse, currentSignature, product }) => {
          const signature = eventResponse.payload?.signature?.checksum;

          if (signature && signature === currentSignature) {
            return NEVER;
          }

          let signature$ = of('');

          if (signature && signature !== currentSignature) {
            signature$ = this.signatureService.insertSignatureOnDB(signature);
          }

          const insertProductIntoProducts$ =
            this.productsService.insertProduct(product);
          const updateProductsIntoProductFolders$ =
            this.productFolderService.updateProductIntoProductFolder(product);

          return combineLatest([
            insertProductIntoProducts$,
            updateProductsIntoProductFolders$,
            signature$,
          ]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.products} 🍺] [New product created]`
        );
        this.notifyProductCreated();
      });
  }

  /**
   * Listens for product activation/deactivation events from the socket and updates the local state.
   * Handles signature validation and ensures no duplicate processing.
   * Notifies subscribers when a product is activated or deactivated.
   */
  private onProductActivatedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.products} 🍺] [Product activated event initialized]`
    );

    this.socketService
      .receive<ISocketPayload>(SOCKET_PRODUCT_EVENT.activated)
      .pipe(
        catchError((error) => throwError(() => error)),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest({
            eventResponse: of(eventResponse),
            currentSignature: this.signatureService.getSignatureFromIndexedDB(),
          })
        ),
        mergeMap(({ eventResponse, currentSignature }) => {
          return combineLatest({
            eventResponse: of(eventResponse),
            currentSignature: of(currentSignature),
            products: this.productsService.getProductsFromLocal(),
            rawProduct: this.productFactoryService.createOne(
              eventResponse.payload.product,
              true
            ),
          });
        }),
        mergeMap(
          ({ eventResponse, currentSignature, products, rawProduct }) => {
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let product = products.find(
              (product: Product) => product?._id === rawProduct?._id
            );

            if (product) {
              product.activated = rawProduct.activated;
            } else if (rawProduct) {
              product = rawProduct;
            } else {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            const updateProductIntoFolders$ =
              this.productFolderService.updateProductIntoProductFolder(product);

            const updateProduct$ = this.productsService.insertProduct(product);

            return combineLatest([
              of(product),
              updateProduct$,
              updateProductIntoFolders$,
              signature$,
            ]);
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([product]: [Product, Product | null, IFolder[], string]) => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.products} 🍺] [Product activated/deactivated]`
        );
        this.notifyProductActivated(product);
      });
  }

  /**
   * Listens for product folder modification events from the socket and updates the local state.
   * Handles signature validation and ensures no duplicate processing.
   * Notifies subscribers when product folders are modified.
   */
  private onProductFoldersModifiedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.products} 🍺] [Product folders modified event initialized]`
    );

    this.socketService
      .receive<ISocketPayload>(SOCKET_PRODUCT_EVENT.foldersModified)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => throwError(() => error)),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            return combineLatest([
              of(eventResponse),
              of(currentSignature),
              this.productsService.getProductsFromLocal(),
            ]);
          }
        ),
        mergeMap(
          ([eventResponse, currentSignature, products]: [
            ISocketPayload,
            string,
            Product[]
          ]) => {
            const rawProduct = eventResponse.payload.product as {
              _id: string;
              product_folders: string[];
            };
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            const product = products.find(
              (product: Product) => product?._id === rawProduct?._id
            );

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            if (product) {
              product.product_folders =
                rawProduct.product_folders as unknown as IFolder[];

              const updateProductIntoFolders$ =
                this.productFolderService.updateProductIntoProductFolder(
                  product
                );

              const updateProduct$ =
                this.productsService.insertProduct(product);

              return combineLatest([
                updateProduct$,
                updateProductIntoFolders$,
                signature$,
              ]);
            }

            return NEVER;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.products} 🍺] [Product folders modified]`
        );
        this.notifyProductFolderModified();
      });
  }
}
